/**
 * Nudge History page — /dashboard/history
 * Shows a chronological timeline of the current user's received nudges with status badges.
 */

import { useEffect, useState } from 'react'
import { CheckCircle2, Clock, MinusCircle, BookOpen, ChevronDown, Check } from 'lucide-react'
import { api, type NudgeDelivery } from '../lib/api'
import confetti from 'canvas-confetti'
import { toast } from '../store/toastStore'
import { CATEGORY_METADATA } from '@duva/shared'


function outcomeIcon(outcome: string | null) {
  if (outcome === 'completed') return <CheckCircle2 size={16} className="text-green-600 shrink-0" />
  if (outcome === 'snoozed') return <Clock size={16} className="text-amber-500 shrink-0" />
  return <MinusCircle size={16} className="text-ink-soft shrink-0" />
}

function outcomeBadge(outcome: string | null) {
  if (outcome === 'completed') return <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 font-medium">Done</span>
  if (outcome === 'snoozed') return <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">Snoozed</span>
  return <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-warm-inset text-ink-muted border border-warm-border font-medium">Pending</span>
}

function groupByWeek(deliveries: NudgeDelivery[]): { label: string; items: NudgeDelivery[] }[] {
  const groups: Record<string, NudgeDelivery[]> = {}
  for (const d of deliveries) {
    const date = new Date(d.sent_at)
    const monday = new Date(date)
    monday.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7))
    const key = monday.toISOString().slice(0, 10)
    if (!groups[key]) groups[key] = []
    groups[key].push(d)
  }

  const now = new Date()
  const thisMonday = new Date(now)
  thisMonday.setUTCDate(now.getUTCDate() - ((now.getUTCDay() + 6) % 7))
  const thisWeekKey = thisMonday.toISOString().slice(0, 10)

  const lastMondayDate = new Date(thisMonday)
  lastMondayDate.setUTCDate(thisMonday.getUTCDate() - 7)
  const lastWeekKey = lastMondayDate.toISOString().slice(0, 10)

  return Object.entries(groups)
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([key, items]) => ({
      label: key === thisWeekKey ? 'This week' : key === lastWeekKey ? 'Last week' : `Week of ${new Date(key).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`,
      items,
    }))
}

const skeletonRows = [1, 2, 3, 4, 5]

export function NudgeHistoryPage() {
  const [deliveries, setDeliveries] = useState<NudgeDelivery[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set(['This week', 'Last week']))

  useEffect(() => {
    api.getNudgeHistory()
      .then(r => setDeliveries(r.deliveries))
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load history'))
      .finally(() => setLoading(false))
  }, [])

  const groups = groupByWeek(deliveries)

  function toggleWeek(label: string) {
    setExpandedWeeks(prev => {
      const next = new Set(prev)
      if (next.has(label)) next.delete(label)
      else next.add(label)
      return next
    })
  }

  async function handleMarkDone(deliveryId: string) {
    try {
      const res = await api.updateDelivery(deliveryId, { outcome: 'completed' })
      if (res.isFirstNudge) {
        confetti({
          particleCount: 150,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#2A7A8A', '#B8C7B1', '#FFD700']
        })
        toast.success("First nudge completed! You've started a great habit. 🥳")
      } else {
        toast.success("Nudge marked as done")
      }
      // Refresh local state
      setDeliveries(prev => prev.map(d => d.id === deliveryId ? { ...d, outcome: 'completed' } : d))
    } catch (err: any) {
      toast.error(err.message || "Failed to update nudge")
    }
  }

  return (
    <div className="p-6 md:p-8 max-w-2xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: '#EBF6FB' }}
          >
            <BookOpen size={18} style={{ color: '#2A7A8A' }} />
          </div>
          <h1
            className="text-2xl font-bold text-ink"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Nudge History
          </h1>
        </div>
        <p className="text-sm text-ink-muted ml-12">
          Your personal record of received nudges and how you engaged with them.
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {skeletonRows.map(i => (
            <div
              key={i}
              className="h-16 rounded-2xl bg-warm-card border border-warm-border animate-pulse"
            />
          ))}
        </div>
      ) : deliveries.length === 0 ? (
        <div
          className="rounded-2xl border border-warm-border p-10 text-center shadow-card"
          style={{ background: '#FDFCF8' }}
        >
          <div className="text-3xl mb-3">🕊</div>
          <p className="text-sm font-medium text-ink mb-1">No nudges yet</p>
          <p className="text-xs text-ink-muted">
            Your nudge history will appear here once you start receiving them.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {groups.map(({ label, items }) => {
            const isOpen = expandedWeeks.has(label)
            const completedCount = items.filter(d => d.outcome === 'completed').length
            return (
              <div
                key={label}
                className="rounded-2xl border border-warm-border overflow-hidden shadow-card"
                style={{ background: '#FDFCF8' }}
              >
                <button
                  type="button"
                  onClick={() => toggleWeek(label)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-warm-inset transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-ink">{label}</span>
                    <span className="text-xs text-ink-muted">
                      {items.length} nudge{items.length !== 1 ? 's' : ''}
                      {completedCount > 0 && (
                        <span className="ml-1.5 text-green-700 font-medium">·  {completedCount} done</span>
                      )}
                    </span>
                  </div>
                  <ChevronDown
                    size={16}
                    className="text-ink-soft transition-transform"
                    style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0)' }}
                  />
                </button>

                {isOpen && (
                  <div className="border-t border-warm-border divide-y divide-warm-border/60">
                    {items.map(d => {
                      const catMeta = CATEGORY_METADATA[d.category as keyof typeof CATEGORY_METADATA]
                      const catStyle = catMeta ? { bg: catMeta.bg, text: catMeta.text } : { bg: '#f5f5f5', text: '#555' }
                      return (
                        <div key={d.id} className="flex items-start gap-3 px-5 py-3.5">
                          <div className="mt-0.5">{outcomeIcon(d.outcome)}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-ink font-medium leading-snug">{d.title}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span
                                className="text-[11px] px-1.5 py-0.5 rounded-full font-medium capitalize"
                                style={{ background: catStyle.bg, color: catStyle.text }}
                              >
                                {d.category}
                              </span>
                              <span className="text-[11px] text-ink-muted">
                                {new Date(d.sent_at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                              </span>
                            </div>
                          </div>
                          <div className="shrink-0 mt-0.5">
                            {d.outcome ? (
                              outcomeBadge(d.outcome)
                            ) : (
                              <button
                                onClick={() => handleMarkDone(d.id)}
                                className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-brand-teal text-white font-semibold hover:bg-brand-teal/90 transition-all active:scale-95 shadow-sm"
                              >
                                <Check size={12} />
                                Mark as Done
                              </button>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {!loading && deliveries.length > 0 && (
        <p className="text-center text-xs text-ink-muted mt-6">
          Showing your last {deliveries.length} nudge{deliveries.length !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  )
}
